import {toast} from '@wandb/weave/common/components/elements/Toast';
import {useCallback} from 'react';
import {useHistory, useLocation} from 'react-router-dom';

import {useDispatch, useMemoizedSelector} from '../../../../state/hooks';
import {usePanelBankSectionsPagingContext} from '../../../../state/panelbank/PanelBankSectionsPagingContext';
import {usePart} from '../../../../state/views/hooks';
import type * as PanelTypes from '../../../../state/views/panel/types';
import * as PanelBankConfigActions from '../../../../state/views/panelBankConfig/actions';
import * as PanelBankSectionConfigActions from '../../../../state/views/panelBankSectionConfig/actions';
import {makeMaybeSectionSelector} from '../../../../state/views/panelBankSectionConfig/selectors';
import {getPanelSpec, type LayedOutPanel} from '../../../../util/panelTypes';
import {getPanelLinkById} from '../../../Panel/FullScreenPanel/fullScreenUtils';
import {SectionPanelSorting} from '../../../PanelBank/types';
import {useWorkspaceRefsContext} from '../../../Workspace/WorkspaceRefsContext';
import {useSortAlphabetically} from '../../Settings/hooks';
import {useSectionSettingsContextMaybe} from '../../Settings/SectionSettingsContextProvider';
import {useWorkspaceDrawerContext} from '../../WorkspaceDrawerContextProvider';
import {useShouldOpenSettingsDrawerOnCreate} from './useShouldOpenSettingsDrawerOnCreate';

export function useOnOK(
  newPanel: LayedOutPanel | undefined,
  setNewPanel: (panel: LayedOutPanel | undefined) => void,
  groupTitle: string | undefined
) {
  const dispatch = useDispatch();
  const {setCurrentPageForSectionRefID} = usePanelBankSectionsPagingContext();
  const {
    openWorkspaceDrawer,
    closeWorkspaceDrawer,
    targetSectionRef: drawerSectionRef,
  } = useWorkspaceDrawerContext();

  const isGlobalAdd = drawerSectionRef == null;

  const sortAlphabetically = useSortAlphabetically();
  const shouldOpenSettingsDrawerOnCreate = useShouldOpenSettingsDrawerOnCreate(
    newPanel?.viewType
  );

  const history = useHistory();
  const location = useLocation();

  const {panelBankConfigRef} = useWorkspaceRefsContext();
  // oxlint-disable-next-line wandb/no-use-part-in-contexts -- TODO: migrate to reselect selectors
  const panelBankPart = usePart(panelBankConfigRef);

  // If panel is to be added to an existing section, a SectionSettingsContext
  // will be available, and the sectionRef from that context should be used.
  // This may be different from the targetSectionRef in WorkspaceDrawerContext
  // because an existing/matching section may be available even for global add.
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  // The drawer keeps its target sectionRef in React state, so the section can be
  // deleted (undo of an add, for instance) while this hook is still mounted.
  const sectionPart = useMemoizedSelector(makeMaybeSectionSelector, sectionRef);

  return useCallback(
    (panelRef: PanelTypes.Ref) => {
      if (newPanel == null) {
        return;
      }
      const panelSpec = getPanelSpec(newPanel.viewType);
      const panelID = newPanel.__id__;
      setNewPanel(undefined);

      // `panelRef` is the temporary editor ref from `useNewObjectRef`.
      // `addPanelByRef` / `addSection` re-normalize panels and assign new ids;
      // settings and selectors must use the canonical ref from the bank.
      let drawerPanelRef: PanelTypes.Ref = panelRef;
      // A section that is gone from state can't receive the panel, so add it to a
      // new section instead of dispatching against a stale ref.
      const targetSectionRef = sectionPart != null ? sectionRef : undefined;
      let drawerSectionRef: typeof sectionRef | undefined = targetSectionRef;

      if (targetSectionRef) {
        dispatch(
          PanelBankSectionConfigActions.addPanelByRef(
            targetSectionRef,
            panelRef,
            {
              shouldAlphaInsert: sortAlphabetically,
              panelBankConfigRef,
              callbackFn: (_panel, newPanelRef) => {
                drawerPanelRef = newPanelRef;
              },
            }
          )
        );
        if (isGlobalAdd && sectionPart?.name) {
          toast(`Panel added to ${sectionPart.name} section`);
        } else if (sectionPart?.sorted === SectionPanelSorting.Alphabetical) {
          toast('Panel added alphabetically to section');
        } else {
          // default sorting adds new panels to the first page
          setCurrentPageForSectionRefID(targetSectionRef.id, 0);
        }
      } else {
        dispatch(
          PanelBankConfigActions.addSection(
            panelBankConfigRef,
            panelBankPart.sectionRefs[0],
            {
              newSectionName: groupTitle,
              panelRefs: [panelRef],
              callbackFn: (newSectionRef, newPanelRefs) => {
                drawerSectionRef = newSectionRef;
                const inserted = newPanelRefs[0];
                if (inserted != null) {
                  drawerPanelRef = inserted;
                }
              },
            }
          )
        );
        toast(`Panel added to ${groupTitle} section`);
      }
      if (
        shouldOpenSettingsDrawerOnCreate &&
        panelSpec.settingsDrawerContent &&
        drawerSectionRef != null
      ) {
        const link = getPanelLinkById(panelID, location);
        history.push(link);
        openWorkspaceDrawer(
          panelSpec.settingsDrawerContent,
          drawerSectionRef,
          drawerPanelRef
        );
      } else {
        closeWorkspaceDrawer();
      }
    },
    [
      newPanel,
      setNewPanel,
      groupTitle,
      isGlobalAdd,
      sortAlphabetically,
      shouldOpenSettingsDrawerOnCreate,
      dispatch,
      panelBankConfigRef,
      panelBankPart,
      sectionRef,
      sectionPart,
      openWorkspaceDrawer,
      closeWorkspaceDrawer,
      history,
      location,
      setCurrentPageForSectionRefID,
    ]
  );
}
